<template>
  <div class="view-funding view-dark-bg">
    <LandingHero :hero="hero" />

    <b-container ref="list">
      <b-row class="pb-60">
        <b-col lg="3">
          <FundingSearch v-model="filters.search" class="mb-30" />
          <SearchFilter
            v-model="filters.countryCodes"
            label="Countries"
            label-key="text"
            :options="countries"
          />

          <SearchFilter
            v-model="filters.categories"
            label="Investor type"
            :options="categories"
          />

          <SearchFilter
            v-model="filters.stages"
            label="Invests in stage"
            :options="stages"
          />

          <SearchFilter
            v-model="filters.industries"
            label="Industry"
            :options="industries"
          />

          <!--           <SearchFilter
            v-model="filters.sdgs"
            label="SDGs"
            :options="sdgs"
            add-numbers
            :display-limit="0"
            collapsible
            collapsed
          /> -->

          <SearchFilter
            v-model="filters.investmentRanges"
            label="Ticket size"
            :options="investmentRanges"
            collapsible
            collapsed
          />
        </b-col>

        <b-col>
          <header
            class="mt-16 mt-lg-0 pt-10 pt-lg-0 pb-4 pb-lg-0 border-md-down-top"
          >
            <b-row class="mt-16 mt-lg-0 mb-16 mb-lg-30">
              <b-col class="toolbar d-flex align-items-center">
                <h6>
                  <span class="hidden-md-down">Showing:</span>
                  {{ investors ? investors.total : 0 }}
                  <span class="hidden-md-down">filtered</span> investors
                </h6>
              </b-col>
            </b-row>
          </header>
          <content>
            <SearchNoResults v-if="!list.length" type="investors" />
            <b-row v-else>
              <b-col
                v-for="investor in list"
                :key="investor.id"
                md="6"
                class="mb-30"
              >
                <FundingListItem :investor="investor" />
              </b-col>
            </b-row>
          </content>

          <footer v-if="investors && investors.pages > 1" class="mt-40">
            <SearchPagination v-model="page" :result="investors" />
          </footer>
        </b-col>
      </b-row>

      <div class="beware">
        IS YOUR PROFILE MISSING? - If you are an investor missing from this
        overview, please join here:
        <router-link to="/investors/join">thehub.io/investors/join</router-link>
        <br />
        BE AWARE – that thehub.io aims to shed lights on the funding jungle,
        providing you with an overview of some of the available funding sources,
        offering guidance in your search for the appropriate one and hence
        making it easier for you to navigate through the various possibilities.
        However, it is neither thehub.io’s ambition to show an exhaustive and
        complete list nor advising on binding offers.
      </div>
    </b-container>
  </div>
</template>

<script>
import castArray from 'lodash/castArray'
import get from 'lodash/get'
import omitBy from 'lodash/omitBy'
import clone from 'lodash/clone'
import FundingSearch from '@/components/funding/FundingSearch'
import LandingHero from '@/components/landing/LandingHero'
import SearchFilter from '@/components/search/SearchFilter'
import FundingListItem from '@/components/funding/FundingListItem'
import { isEmptyDeep } from '@/helpers/helpers'
import { buildMetaHead } from '@/helpers/meta'
import SearchNoResults from '@/components/search/SearchNoResults'
import SearchPagination from '@/components/search/SearchPagination'

export default {
  name: 'PageFunding',
  components: {
    SearchPagination,
    FundingSearch,
    SearchFilter,
    LandingHero,
    FundingListItem,
    SearchNoResults
  },
  data() {
    const countryCode = this.$store.getters['meta/sitePreferredCountryCode']

    return {
      hero: {
        title: 'Connect to leading investors',
        subtitle:
          'Unsure which funding opportunity is right for you? Use our investor matching tool to help you on to your next step',
        button: {
          link: '/funding/quiz',
          label: 'Find investors'
        },
        caption: 'Takes less than ~2 min to complete',
        image: require('~/assets/components/funding/girl.png')
      },
      page: 1,
      filters: {
        industries: castArray(get(this.$route, 'query.industries', [])),
        investmentRanges: castArray(
          get(this.$route, 'query.investmentRanges', [])
        ),
        categories: castArray(get(this.$route, 'query.categories', [])),
        countryCodes: castArray(
          get(this.$route, 'query.countryCodes', [countryCode])
        ),
        search: get(this.$route, 'query.search'),
        //sdgs: castArray(get(this.$route, 'query.sdgs', [])),
        stages: castArray(get(this.$route, 'query.stages', []))
      },
      industryStats: []
    }
  },
  computed: {
    params() {
      const filters = omitBy(clone(this.filters), isEmptyDeep)
      if (this.page >= 2) {
        filters.page = this.page
      }

      return filters
    },
    investors() {
      return this.$store.state.investors.investors
    },
    list() {
      return this.investors ? this.investors.docs : []
    },
    countries() {
      return this.$store.getters['meta/siteCountriesExNI'].map((item) => ({
        ...item,
        count: get(
          this.investors,
          'suggestions.countries.' + item.value,
          undefined
        )
      }))
    },
    industries() {
      return this.$store.getters['meta/industries']
        .filter((item) => {
          const stats = this.industryStats.find(
            (stat) => stat.industry === item.value
          )

          return stats && stats.exists
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
        .map((item) => ({
          ...item,
          count: get(
            this.investors,
            'suggestions.industries.' + item.value,
            undefined
          )
        }))
    },
    categories() {
      return this.$store.getters['meta/investorCategories']
        .slice(0)
        .sort((a, b) => (a.label > b.label ? 1 : -1))
        .map((item) => ({
          ...item,
          count: get(
            this.investors,
            'suggestions.categories.' + item.value,
            undefined
          )
        }))
    },
    investmentRanges() {
      return this.$store.getters['meta/investorInvestmentRanges'].map(
        (item) => ({
          ...item,
          count: get(
            this.investors,
            'suggestions.investmentRanges.' + item.value,
            undefined
          )
        })
      )
    },
    stages() {
      return this.$store.getters['meta/companyStages'].map((item) => ({
        ...item,
        count: get(
          this.investors,
          'suggestions.stages.' + item.value,
          undefined
        )
      }))
    }
    /*     sdgs() {
      return this.$store.getters['meta/sdgs']
    } */
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        // whenever a filter is changed, reset offset (page) to beginning
        this.page = 1

        this.doSearch()
      }
    },
    page() {
      this.doSearch()

      this.scrollToTop()
    }
  },
  async asyncData({ $axios, query, store, error }) {
    try {
      const params = {
        includeSuggestions: true,
        ...query
      }

      // if no `countryCodes` defined, default to ip detected.
      const countryCode = store.getters['meta/sitePreferredCountryCode']
      if (!params.countryCodes) {
        params.countryCodes = [countryCode]
      }

      const [industryStats] = await Promise.all([
        $axios.$get('investors/industries'),
        store.dispatch('investors/find', params)
      ])

      return {
        industryStats
      }
    } catch (err) {
      error(err)
    }
  },
  head() {
    return buildMetaHead({
      title: 'Need funding? Use our tool to find an investor',
      description:
        'Build your funding pipeline with our database of 1,150+ investors. Use our matching tool to find the perfect investors within your vertical and investment needs',
      path: this.$route.path,
      imageUrl: process.env.BASE_URL + '/images/thehub_2022_find_funding.jpg'
    })
  },
  methods: {
    scrollToTop() {
      if (this.$refs.list && this.$refs.list.scrollIntoView) {
        document.body.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    async doSearch() {
      await this.$store.dispatch('investors/find', {
        includeSuggestions: true,
        ...this.params
      })

      this.$router.push({
        query: this.params
      })
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .hero {
  .hero-content {
    max-width: none;
  }

  .hero-pattern > .man {
    position: absolute !important;
    bottom: -3rem !important;
    left: -1rem !important;
    height: auto !important;
    width: auto !important;
    max-width: 341px !important;
  }
}

.toolbar {
  height: 52px;
}

.view-funding {
  &__more {
    text-align: center;
  }
}

.beware {
  border-top: 1px solid #979797;
  padding: rem(30px) 0;
  color: transparentize(#000, 0.46);
  font-size: rem(16px);
  line-height: rem(20px);
  letter-spacing: -0.5px;
}
</style>
